import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "../lib/axios";

export interface AICreditBalance {
  baseMonthlyCredits: number;
  monthlyUsedCredits: number;
  monthlyRemainingCredits: number;
  purchasedCreditsBalance: number;
  totalAvailableCredits: number;
  totalUsedCredits: number;
  updatedAt: string;
  nextResetDate: string;
}

export interface AICreditTransaction {
  id: number;
  transactionType: string;
  feature: string;
  amount: number;
  balanceAfter: number;
  description: string;
  createdAt: string;
}

interface BalanceApiEnvelope {
  success?: boolean;
  data?: Record<string, unknown>;
  [key: string]: unknown;
}

interface TransactionsApiEnvelope {
  success?: boolean;
  transactions?: Array<Record<string, unknown>>;
  data?: Array<Record<string, unknown>>;
  [key: string]: unknown;
}

const toNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeTransaction = (tx: Record<string, unknown>): AICreditTransaction => ({
  id: toNumber(tx.id),
  transactionType: String(tx.transactionType ?? ""),
  feature: String(tx.feature ?? ""),
  amount: toNumber(tx.amount),
  balanceAfter: toNumber(tx.balanceAfter),
  description: String(tx.description ?? ""),
  createdAt: String(tx.createdAt ?? tx.transactionDate ?? new Date().toISOString()),
});

const normalizeTransactions = (payload: unknown): AICreditTransaction[] => {
  if (Array.isArray(payload)) {
    return payload.map((tx) => normalizeTransaction((tx ?? {}) as Record<string, unknown>));
  }

  const envelope = (payload ?? {}) as TransactionsApiEnvelope;
  const list = Array.isArray(envelope.transactions)
    ? envelope.transactions
    : Array.isArray(envelope.data)
      ? envelope.data
      : [];

  return list.map((tx) => normalizeTransaction((tx ?? {}) as Record<string, unknown>));
};

const normalizeBalance = (payload: unknown, transactions: AICreditTransaction[]): AICreditBalance => {
  const envelope = (payload ?? {}) as BalanceApiEnvelope;
  const data = (envelope.data && typeof envelope.data === "object"
    ? envelope.data
    : envelope) as Record<string, unknown>;

  const totalAvailableCredits = toNumber(
    data.totalAvailableCredits ?? data.totalAvailable ?? data.availableCredits,
  );
  const purchasedCreditsBalance = toNumber(data.purchasedCreditsBalance ?? data.purchasedBalance);

  const explicitMonthlyUsed = toNumber(data.monthlyUsedCredits ?? data.monthlyUsed, 0);
  const usageFromTransactions = transactions
    .filter((tx) => tx.transactionType.toLowerCase() === "usage" || tx.amount < 0)
    .reduce((sum, tx) => sum + Math.abs(tx.amount), 0);
  const monthlyUsedCredits = explicitMonthlyUsed > 0 ? explicitMonthlyUsed : usageFromTransactions;

  const explicitBase = toNumber(data.baseMonthlyCredits ?? data.baseCredits);
  const remainingFromTotal = Math.max(0, totalAvailableCredits - purchasedCreditsBalance);
  const explicitRemaining = toNumber(data.monthlyRemainingCredits ?? data.remainingThisMonth, remainingFromTotal);

  const baseMonthlyCredits = explicitBase > 0 ? explicitBase : explicitRemaining + monthlyUsedCredits;
  const monthlyRemainingCredits = explicitRemaining > 0 ? explicitRemaining : Math.max(0, baseMonthlyCredits - monthlyUsedCredits);

  return {
    baseMonthlyCredits,
    monthlyUsedCredits,
    monthlyRemainingCredits,
    purchasedCreditsBalance,
    totalAvailableCredits,
    totalUsedCredits: toNumber(data.totalUsedCredits ?? data.totalUsed, monthlyUsedCredits),
    updatedAt: String(data.updatedAt ?? new Date().toISOString()),
    nextResetDate: String(data.nextResetDate ?? ""),
  };
};

export const useAICredits = () => {
  const queryClient = useQueryClient();
  const location = useLocation();

  // Only fetch AI credits when user is an authenticated restaurant user.
  // Skip on admin, login, register, captain, customer and public pages.
  const shouldFetch = useMemo(() => {
    if (typeof window === "undefined") return false;
    const token = localStorage.getItem("token");
    if (!token) return false;

    const path = location.pathname;
    if (
      path.startsWith("/willovate-admin") ||
      path.startsWith("/captain") ||
      path.startsWith("/customer") ||
      path.startsWith("/r/") ||
      path === "/login" ||
      path === "/register" ||
      path.startsWith("/restaurant-onboarding") ||
      path.startsWith("/pending-approval")
    ) {
      return false;
    }

    const businessIdRaw =
      localStorage.getItem("businessId") || localStorage.getItem("restaurantId");
    const businessId = Number(businessIdRaw);
    if (!Number.isFinite(businessId) || businessId <= 0) return false;

    return true;
  }, [location.pathname]);

  // Single query that fetches both balance + transactions in one shot.
  // historyQuery is a derived view of the same data — no duplicate API call.
  const combinedQuery = useQuery({
    queryKey: ["ai-credits", "data"],
    queryFn: async () => {
      const [balanceRes, txRes] = await Promise.all([
        axiosInstance.get("/api/resto/ai-credits/balance"),
        axiosInstance.get("/api/resto/ai-credits/transactions?pageSize=50"),
      ]);
      const transactions = normalizeTransactions(txRes.data);
      const balance = normalizeBalance(balanceRes.data, transactions);
      return { balance, transactions };
    },
    retry: false,
    enabled: shouldFetch,
    staleTime: 2 * 60 * 1000, // treat as fresh for 2 minutes
  });

  // Derived views — same shape consumers expect, zero extra API calls
  const balanceQuery = {
    data: combinedQuery.data?.balance,
    isLoading: combinedQuery.isLoading,
    isError: combinedQuery.isError,
    error: combinedQuery.error,
    isSuccess: combinedQuery.isSuccess,
    refetch: combinedQuery.refetch,
  };

  const historyQuery = {
    data: combinedQuery.data?.transactions,
    isLoading: combinedQuery.isLoading,
    isError: combinedQuery.isError,
    error: combinedQuery.error,
    isSuccess: combinedQuery.isSuccess,
    refetch: combinedQuery.refetch,
  };

  const rechargeMutation = useMutation({
    mutationFn: async (amount: number) => {
      const { data } = await axiosInstance.post(
        "/api/resto/ai-credits/recharge",
        { amount }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-credits"] });
    },
  });

  const hasAICredits = () => {
    return (balanceQuery.data?.totalAvailableCredits ?? 0) > 0;
  };

  return {
    balanceQuery,
    historyQuery,
    rechargeMutation,
    hasAICredits,
  };
};
